'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { RefreshCw, CheckCircle2 } from 'lucide-react';

interface ClickCaptchaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  images: string[];
  correctIndices: number[];
  text: string;
  onVerify: (selectedIndices: number[]) => Promise<boolean>;
  onRefresh?: () => void;
}

export function ClickCaptchaDialog({
  open,
  onOpenChange,
  images,
  correctIndices,
  text,
  onVerify,
  onRefresh,
}: ClickCaptchaDialogProps) {
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string>('');

  const handleImageClick = (index: number) => {
    if (isVerified || isVerifying) return;

    setSelectedIndices((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
    setError('');
  };

  const handleVerify = async () => {
    if (selectedIndices.length === 0) {
      setError('请至少选择一张图片');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const success = await onVerify(selectedIndices);
      if (success) {
        setIsVerified(true);
        // 立即关闭对话框，不延迟，让用户可以直接登录
        onOpenChange(false);
      } else {
        setError('验证失败，请重试');
        setSelectedIndices([]);
      }
    } catch {
      setError('验证失败，请重试');
      setSelectedIndices([]);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleReset = () => {
    setSelectedIndices([]);
    setIsVerified(false);
    setIsVerifying(false);
    setError('');
    onRefresh?.();
  };

  useEffect(() => {
    if (open) {
      setSelectedIndices([]);
      setIsVerified(false);
      setIsVerifying(false);
      setError('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>图片验证</DialogTitle>
          <DialogDescription>{text}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            {images.map((image, index) => {
              const isSelected = selectedIndices.includes(index);
              const isCorrect = correctIndices.includes(index);

              return (
                <div
                  key={index}
                  className={`relative aspect-square border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                    isSelected
                      ? 'border-blue-500 ring-2 ring-blue-300 dark:ring-blue-700'
                      : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
                  } ${isVerified && isCorrect ? 'ring-2 ring-green-500' : ''}`}
                  onClick={() => handleImageClick(index)}
                >
                  <Image
                    src={image}
                    alt={`图片 ${index + 1}`}
                    fill
                    className="object-cover"
                    unoptimized
                  />

                  {isSelected && (
                    <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                  )}

                  {isVerified && isCorrect && (
                    <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                    </div>
                  )}

                  {isVerifying && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isVerifying || isVerified}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              刷新
            </Button>

            <div className="text-sm text-gray-600 dark:text-gray-400">
              已选择 {selectedIndices.length} 张图片
            </div>

            <Button
              type="button"
              onClick={handleVerify}
              disabled={selectedIndices.length === 0 || isVerifying || isVerified}
            >
              {isVerifying ? '验证中...' : isVerified ? '验证成功' : '验证'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

